import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";

// Initialize mermaid with custom theme
mermaid.initialize({
  startOnLoad: false,
  theme: "base",
  themeVariables: {
    primaryColor: "#8b5cf6",
    primaryTextColor: "#fff",
    primaryBorderColor: "#7c3aed",
    secondaryColor: "#f3f4f6",
    secondaryTextColor: "#374151",
    tertiaryColor: "#fef3c7",
    lineColor: "#6b7280",
    textColor: "#374151",
    mainBkg: "#f9fafb",
    nodeBorder: "#d1d5db",
    clusterBkg: "#f3f4f6",
    clusterBorder: "#e5e7eb",
    edgeLabelBackground: "#fff",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  flowchart: {
    htmlLabels: true,
    curve: "basis",
    padding: 15,
  },
  securityLevel: "loose",
});

// Helper to provide user-friendly error messages
const getErrorHelp = (errorMessage) => {
  if (errorMessage.includes("Parse error")) {
    if (errorMessage.includes("<br") || errorMessage.includes("&lt;")) {
      return "Don't use HTML tags like <br/>. For multi-line text, use quotes: A[\"Line 1\\nLine 2\"]";
    }
    if (errorMessage.includes("→") || errorMessage.includes("←")) {
      return "Don't use special arrow characters (→). Use --> for arrows instead.";
    }
    if (errorMessage.includes("(") || errorMessage.includes(")")) {
      return "Text with parentheses needs quotes: A[\"Text (with parens)\"]";
    }
    return "Check your syntax. Make sure to use quotes around text with special characters.";
  }
  return null;
};

export default function MermaidDiagram({ chart, className = "" }) {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState("");
  const [error, setError] = useState(null);
  const [errorHelp, setErrorHelp] = useState(null);

  useEffect(() => {
    const renderChart = async () => {
      if (!chart || !containerRef.current) return;

      try {
        setError(null);
        setErrorHelp(null);
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(id, chart);
        setSvg(svg);
      } catch (err) {
        console.error("Mermaid rendering error:", err);
        const message = err.message || "Failed to render diagram";
        setError(message);
        setErrorHelp(getErrorHelp(message));
      }
    };

    renderChart();
  }, [chart]);

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-red-600 text-sm font-medium">Diagram Error</p>
        <p className="text-red-500 text-xs mt-1 font-mono">{error}</p>
        {errorHelp && (
          <p className="text-amber-700 text-xs mt-2 bg-amber-50 p-2 rounded">
            💡 <strong>Tip:</strong> {errorHelp}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`mermaid-container overflow-auto ${className}`}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
